// Step-up approvals (NEXUS-762): an agent asked Nexus for a time-boxed admin elevation and Helm shows it as an
// approval of kind 'step_up'. Approving it here is: passkey step-up -> Nexus approve endpoint -> Helm marks it decided.
// Helm's server never calls Nexus with admin power; THIS client does, with the owner's own passkey-fresh session.
//
// TODO (later slices): native Mac/iOS passkey approval, push notification to the phone, auto-request from agents.
import { AUTH_URL } from './config';
import { ApiError, callFn, getToken } from './api';
import { assertPasskey } from './passkey';
import type { WaitingItem } from './types';

export interface StepUpPlan { elevationId: string; actionClass: string; minutes: number }

/** The plan of a step_up item, or null when it is some other approval / the plan is malformed. */
export function stepUpPlan(w: Pick<WaitingItem, 'item' | 'kind' | 'plan'>): StepUpPlan | null {
  if (w.item !== 'approval' || w.kind !== 'step_up' || !w.plan || typeof w.plan !== 'object') return null;
  const p = w.plan as Record<string, unknown>;
  if (typeof p.elevationId !== 'string' || typeof p.actionClass !== 'string') return null;
  return { elevationId: p.elevationId, actionClass: p.actionClass, minutes: typeof p.minutes === 'number' ? p.minutes : 60 };
}

export interface StepUpDeps {
  passkey: () => Promise<void>;
  approveElevation: (elevationId: string) => Promise<void>;
  decide: (payload: Record<string, unknown>) => Promise<unknown>;
}

async function nexusApprove(elevationId: string): Promise<void> {
  const res = await fetch(AUTH_URL(`elevations/${encodeURIComponent(elevationId)}/approve`), {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken() ?? ''}` },
  });
  if (res.ok) return;
  let j: { message?: string; code?: string } = {};
  try { j = JSON.parse(await res.text()); } catch { /* non-JSON */ }
  throw new ApiError(res.status, j.message ?? `HTTP ${res.status}`, j.code, j);
}

export const defaultStepUpDeps: StepUpDeps = {
  passkey: assertPasskey,
  approveElevation: nexusApprove,
  decide: (payload) => callFn('approval_decide', payload),
};

/**
 * Approve a step_up item. Order matters: nothing is marked decided in Helm unless Nexus accepted the approval,
 * and the approval is never attempted without a fresh passkey step-up first.
 */
export async function approveStepUp(w: WaitingItem, note: string | undefined, deps: StepUpDeps = defaultStepUpDeps): Promise<void> {
  const plan = stepUpPlan(w);
  if (!plan) throw new Error('This approval has no elevation attached.');
  await deps.passkey();
  await deps.approveElevation(plan.elevationId);
  await deps.decide({ id: w.id, decision: 'approved', elevationApproved: true, ...(note ? { note } : {}) });
}
